import type { PlatformActionProposal } from '../models/platformAction.types'
import type { ProposeBodyDto } from '../models/platformAction.wire'
import { adaptActionRequest } from '../service/adaptActionRequest'
import { classifyError } from '../service/classifyError'

import type { FlowResult, ProposeActionApi } from './platformActionFlow.types'

export interface ProposeActionFlowDeps {
  api: ProposeActionApi
}

/**
 * Step 1 of the propose → confirm handshake: creates the request in `pending_approval` and returns
 * its preview plus the `confirm_token`.
 *
 * The token is bound to exactly this payload and lives 15 minutes; it is handed to the caller
 * rather than stored globally, so it dies with the modal that will spend it.
 */
export class ProposeActionFlow {
  constructor(private readonly deps: ProposeActionFlowDeps) {}

  async execute(
    workspaceId: number,
    body: ProposeBodyDto,
    signal?: AbortSignal,
  ): Promise<FlowResult<PlatformActionProposal>> {
    try {
      const result = await this.deps.api.propose(workspaceId, body, signal)
      return {
        ok: true,
        value: {
          request: adaptActionRequest(result.request),
          confirmToken: result.confirm_token,
        },
      }
    } catch (cause) {
      return { ok: false, error: classifyError(cause) }
    }
  }
}
